import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import withSessionCheck from '../../../withSessionCheck';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ActivateIcon from '@mui/icons-material/CheckCircle';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { fetchData, deleteData, activateData, postData, updateData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import Button from '@mui/material/Button';
import { Tooltip } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import CopyrightIcon from '@mui/icons-material/Copyright';

function BankIndex() {
    const [data, setData] = useState([]);
    const [deleteItemId, setDeleteItemId] = useState(null);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const history = useHistory();
    const { isAuthenticated, companyId, userId, token, logout } = useAuth();
    const [loading, setLoading] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isUpdateLoading, setIsUpdateLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [errorMessageEdit, setErrorMessageEdit] = useState("");

    // Form fields for Bank
    const [Name, setName] = useState('');
    const [AccountNumber, setAccountNumber] = useState('');
    const [BranchName, setBranchName] = useState('');
    const [IFSCCode, setIFSCCode] = useState('');
    const [AddressLine1, setAddressLine1] = useState('');
    const [AddressLine2, setAddressLine2] = useState('');
    const [ContactNumber, setContactNumber] = useState('');

    useEffect(() => {
        if (isAuthenticated && tokenIsExpired(token)) {
            logout();
            return;
        }
        fetchtableData();
        setLoading(true);
    }, [isAuthenticated, companyId, userId, token, logout]);

    const [searchQuery, setSearchQuery] = useState('');
    const [isFocused, setIsFocused] = useState(false);

    const handleSearch = (event) => {
        setSearchQuery(event.target.value);
    };

    const filteredData = data.filter((item) =>
        Object.values(item).some((value) =>
            String(value).toLowerCase().includes(searchQuery.toLowerCase())
        )
    );

    const fetchtableData = async () => {
        setLoading(true);
        fetchData('BankApi', `/${companyId}`, token)
            .then((response) => {
                console.log('Bank API Response:', response);
                if (response.responseCode === '1111') {
                    setData(response.response_data || []);
                } else {
                    setData([]);
                }
            })
            .catch((error) => {
                console.error('Error fetching Bank Details:', error);
                setData([]);
                if (error.response && error.response.status === 401) {
                    logout();
                    history.replace(`/login?redirect=${encodeURIComponent(window.location.pathname)}`);
                }
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const handleDelete = async () => {
        deleteData('BankApi', `${deleteItemId}`, token)
            .then(() => {
                setDeleteItemId(null);
                setConfirmDialogOpen(false);
                window.location.reload();
            })
            .catch((error) => {
                console.error('Error deleting record:', error);
            });
    };

    const openConfirmDialog = (itemId) => {
        setDeleteItemId(itemId);
        setConfirmDialogOpen(true);
    };

    const closeConfirmDialog = () => {
        setDeleteItemId(null);
        setConfirmDialogOpen(false);
    };

    const [isModalGrpOpen, setIsModalGrpOpen] = useState(false);
    const toggleModalGroup = () => {
        setIsModalGrpOpen(!isModalGrpOpen);
        setErrorMessage('');
        // Reset form fields
        setName('');
        setAccountNumber('');
        setBranchName('');
        setIFSCCode('');
        setAddressLine1('');
        setAddressLine2('');
        setContactNumber('');
    };

    const addGroupRecord = async (e) => {
        e.preventDefault();
        setIsLoading(true);

        if (!Name) {
            setErrorMessage('Bank Name is required!');
            setIsLoading(false);
            return;
        }

        if (!AccountNumber) {
            setErrorMessage('Account Number is required!');
            setIsLoading(false);
            return;
        }

        const Bank = {
            CompanyId: companyId,
            Name,
            AccountNumber,
            BranchName,
            IFSCCode,
            AddressLine1,
            AddressLine2,
            ContactNumber,
            IsActive: true,
            CreatedBy: userId,
            CreatedDate: new Date(),
        };

        postData('BankApi', Bank, token)
            .then(() => {
                setIsModalGrpOpen(false);
                window.location.reload();
            })
            .catch((error) => {
                console.error('Bank Create error:', error);
                setErrorMessage('Failed to create bank record');
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    // EDIT
    const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
    const [updatedData, setUpdateData] = useState({});

    const openDetailModal = async (Id) => {
        try {
            const response = await fetchData('BankApi', `/MasterDetails/${Id}`, token);
            if (response.responseCode === '1111') {
                setUpdateData(response.response_data);
                setIsDetailModalOpen(true);
            }
        } catch (error) {
            console.error('Error fetching Bank Details:', error);
        }
    };

    const toggleModalGrp2 = () => {
        setIsDetailModalOpen(false);
        setErrorMessageEdit('');
    };

    const handleUpdateInputChange = (event) => {
        const { name, value } = event.target;
        setUpdateData((prevDataItem) => ({
            ...prevDataItem,
            [name]: value,
        }));
    };

    const updateRecord = async (event) => {
        event.preventDefault();
        setIsUpdateLoading(true);

        if (!updatedData.Name) {
            setErrorMessageEdit('Bank Name is required!');
            setIsUpdateLoading(false);
            return;
        }

        if (!updatedData.AccountNumber) {
            setErrorMessageEdit('Account Number is required!');
            setIsUpdateLoading(false);
            return;
        }

        try {
            await updateData('BankApi', `${updatedData.Id}`, updatedData, token);
            window.location.reload();
            setIsDetailModalOpen(false);
            fetchtableData();
        } catch (error) {
            console.error('Error updating record:', error);
            setErrorMessageEdit('Failed to update bank record');
        } finally {
            setIsUpdateLoading(false);
        }
    };

    console.log('BankIndex rendering - Data:', data, 'Loading:', loading, 'FilteredData:', filteredData);

    return (
        <>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '12px', gap: '12px' }}>
                <input
                    type="text"
                    placeholder="Search Bank..."
                    value={searchQuery}
                    onChange={handleSearch}
                    onFocus={() => setIsFocused(true)}
                    onBlur={() => setIsFocused(false)}
                    style={{
                        width: isFocused ? '280px' : '220px',
                        height: '32px',
                        border: '2px solid',
                        borderColor: isFocused ? '#667eea' : '#e2e8f0',
                        borderRadius: '12px',
                        padding: '0 16px',
                        fontSize: '14px',
                        transition: 'all 0.3s ease',
                        outline: 'none',
                        background: 'white',
                        boxShadow: isFocused ? '0 4px 12px rgba(102, 126, 234, 0.15)' : 'none',
                    }}
                />
                <Tooltip title="Add New Bank" arrow placement="top">
                    <Button
                        onClick={toggleModalGroup}
                        style={{
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            color: 'white',
                            fontWeight: '600',
                            height: '32px',
                            padding: '0 24px',
                            fontSize: '13px',
                            textTransform: 'none',
                            borderRadius: '12px',
                            boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
                            transition: 'all 0.3s ease',
                            border: 'none',
                            cursor: 'pointer'
                        }}
                    >
                        + New Bank
                    </Button>
                </Tooltip>
            </div>

            {/* Add Bank Modal */}
            {isModalGrpOpen && (
                <div className="fixed inset-0 flex items-center justify-center z-50 p-3" style={{ backgroundColor: 'rgba(0, 0, 0, 0.6)', backdropFilter: 'blur(4px)' }}>
                    <div className="bg-white w-full overflow-hidden" style={{
                        maxWidth: '900px',
                        maxHeight: '85vh',
                        borderRadius: '16px',
                        boxShadow: '0 20px 40px -12px rgba(0, 0, 0, 0.25)',
                    }}>
                        <div style={{
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            padding: '16px 24px',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            height: '55px',
                        }}>
                            <div style={{ height: '20px' }}>
                                <h6 style={{
                                    color: 'white',
                                    fontSize: '15px',
                                    fontWeight: '700',
                                    margin: 0,
                                    marginTop: '-10px'
                                }}>
                                    New Bank
                                </h6>
                                <p style={{
                                    color: 'rgba(255, 255, 255, 0.9)',
                                    fontSize: '13px',
                                    margin: '2px 0 0 0'
                                }}>
                                    Add a new Bank to your system
                                </p>
                            </div>
                            <button onClick={toggleModalGroup} style={{
                                background: 'rgba(255, 255, 255, 0.2)',
                                border: 'none',
                                borderRadius: '50%',
                                width: '36px',
                                height: '36px',
                                cursor: 'pointer',
                                color: 'white',
                                fontSize: '22px',
                            }}>
                                ×
                            </button>
                        </div>

                        <div className="overflow-y-auto" style={{ maxHeight: 'calc(85vh - 150px)', padding: '16px 20px' }}>
                            {errorMessage && (
                                <div style={{
                                    background: 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)',
                                    border: '1px solid #fca5a5',
                                    borderRadius: '12px',
                                    padding: '16px',
                                    marginBottom: '24px',
                                }}>
                                    <p style={{ color: '#991b1b', fontSize: '14px', margin: 0 }}>⚠️ {errorMessage}</p>
                                </div>
                            )}

                            <form onSubmit={addGroupRecord}>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label style={{ display: 'block', color: '#374151', fontSize: '12px', fontWeight: '600', marginBottom: '6px' }}>
                                            Bank Name <span style={{ color: '#ef4444' }}>*</span>
                                        </label>
                                        <input
                                            type="text"
                                            value={Name}
                                            onChange={(e) => setName(e.target.value)}
                                            style={{
                                                width: '100%',
                                                padding: '7px 10px',
                                                border: '2px solid #e5e7eb',
                                                borderRadius: '8px',
                                                fontSize: '13px',
                                                height: '32px'
                                            }}
                                            placeholder="Enter bank name"
                                        />
                                    </div>

                                    <div>
                                        <label style={{ display: 'block', color: '#374151', fontSize: '12px', fontWeight: '600', marginBottom: '6px' }}>
                                            Account Number <span style={{ color: '#ef4444' }}>*</span>
                                        </label>
                                        <input
                                            type="text"
                                            value={AccountNumber}
                                            onChange={(e) => setAccountNumber(e.target.value)}
                                            style={{
                                                width: '100%',
                                                padding: '7px 10px',
                                                border: '2px solid #e5e7eb',
                                                borderRadius: '8px',
                                                fontSize: '13px',
                                                height: '32px'
                                            }}
                                            placeholder="Enter account number"
                                        />
                                    </div>

                                    <div>
                                        <label style={{ display: 'block', color: '#374151', fontSize: '12px', fontWeight: '600', marginBottom: '6px' }}>
                                            Branch Name
                                        </label>
                                        <input
                                            type="text"
                                            value={BranchName}
                                            onChange={(e) => setBranchName(e.target.value)}
                                            style={{
                                                width: '100%',
                                                padding: '7px 10px',
                                                border: '2px solid #e5e7eb',
                                                borderRadius: '8px',
                                                fontSize: '13px',
                                                height: '32px'
                                            }}
                                            placeholder="Enter branch name"
                                        />
                                    </div>

                                    <div>
                                        <label style={{ display: 'block', color: '#374151', fontSize: '12px', fontWeight: '600', marginBottom: '6px' }}>
                                            IFSC Code
                                        </label>
                                        <input
                                            type="text"
                                            value={IFSCCode}
                                            onChange={(e) => setIFSCCode(e.target.value)}
                                            style={{
                                                width: '100%',
                                                padding: '7px 10px',
                                                border: '2px solid #e5e7eb',
                                                borderRadius: '8px',
                                                fontSize: '13px',
                                                height: '32px'
                                            }}
                                            placeholder="Enter IFSC code"
                                        />
                                    </div>

                                    <div>
                                        <label style={{ display: 'block', color: '#374151', fontSize: '12px', fontWeight: '600', marginBottom: '6px' }}>
                                            Address Line 1
                                        </label>
                                        <input
                                            type="text"
                                            value={AddressLine1}
                                            onChange={(e) => setAddressLine1(e.target.value)}
                                            style={{
                                                width: '100%',
                                                padding: '7px 10px',
                                                border: '2px solid #e5e7eb',
                                                borderRadius: '8px',
                                                fontSize: '13px',
                                                height: '32px'
                                            }}
                                            placeholder="Enter address line 1"
                                        />
                                    </div>

                                    <div>
                                        <label style={{ display: 'block', color: '#374151', fontSize: '12px', fontWeight: '600', marginBottom: '6px' }}>
                                            Address Line 2
                                        </label>
                                        <input
                                            type="text"
                                            value={AddressLine2}
                                            onChange={(e) => setAddressLine2(e.target.value)}
                                            style={{
                                                width: '100%',
                                                padding: '7px 10px',
                                                border: '2px solid #e5e7eb',
                                                borderRadius: '8px',
                                                fontSize: '13px',
                                                height: '32px'
                                            }}
                                            placeholder="Enter address line 2"
                                        />
                                    </div>

                                    <div>
                                        <label style={{ display: 'block', color: '#374151', fontSize: '12px', fontWeight: '600', marginBottom: '6px' }}>
                                            Contact Number
                                        </label>
                                        <input
                                            type="text"
                                            value={ContactNumber}
                                            onChange={(e) => setContactNumber(e.target.value)}
                                            style={{
                                                width: '100%',
                                                padding: '7px 10px',
                                                border: '2px solid #e5e7eb',
                                                borderRadius: '8px',
                                                fontSize: '13px',
                                                height: '32px'
                                            }}
                                            placeholder="Enter contact number"
                                        />
                                    </div>
                                </div>
                            </form>
                        </div>

                        <div style={{
                            background: 'linear-gradient(to right, #f9fafb, #f3f4f6)',
                            padding: '16px 24px',
                            display: 'flex',
                            justifyContent: 'flex-end',
                            gap: '12px',
                            borderTop: '1px solid #e5e7eb'
                        }}>
                            <button onClick={toggleModalGroup} disabled={isLoading} style={{
                                padding: '8px 24px',
                                background: 'white',
                                border: '2px solid #e5e7eb',
                                borderRadius: '10px',
                                fontSize: '13px',
                                fontWeight: '600',
                                color: '#6b7280',
                                cursor: isLoading ? 'not-allowed' : 'pointer',
                            }}>
                                Cancel
                            </button>
                            <button onClick={addGroupRecord} disabled={isLoading} style={{
                                padding: '8px 24px',
                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                border: 'none',
                                borderRadius: '10px',
                                fontSize: '13px',
                                fontWeight: '600',
                                color: 'white',
                                cursor: isLoading ? 'not-allowed' : 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                            }}>
                                {isLoading ? (
                                    <>
                                        <CircularProgress size={18} color="inherit" />
                                        <span>Saving...</span>
                                    </>
                                ) : (
                                    <>
                                        <span>✓</span>
                                        <span>Add Bank</span>
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Table */}
            <TableContainer component={Paper} sx={{
                    borderRadius: '8px',
                    overflow: 'hidden',
                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.12)',
                    border: '1px solid #e5e7eb'
                }}>
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell style={{
                                    background: "linear-gradient(180deg, #1e3a8a 0%, #1e40af 100%)",
                                    color: 'white',
                                    fontWeight: '700',
                                    fontSize: '11.5px',
                                    textAlign: 'center',
                                    padding: '2px',
                                }}>Sl.No</TableCell>
                                <TableCell style={{
                                    background: "linear-gradient(180deg, #1e3a8a 0%, #1e40af 100%)",
                                    color: 'white',
                                    fontWeight: '700',
                                    fontSize: '11.5px',
                                    textAlign: 'left',
                                    padding: '2px',
                                }}>Bank Name</TableCell>
                                <TableCell style={{
                                    background: "linear-gradient(180deg, #1e3a8a 0%, #1e40af 100%)",
                                    color: 'white',
                                    fontWeight: '700',
                                    fontSize: '11.5px',
                                    textAlign: 'left',
                                    padding: '2px',
                                }}>Account Number</TableCell>
                                <TableCell style={{
                                    background: "linear-gradient(180deg, #1e3a8a 0%, #1e40af 100%)",
                                    color: 'white',
                                    fontWeight: '700',
                                    fontSize: '11.5px',
                                    textAlign: 'left',
                                    padding: '2px',
                                }}>Branch</TableCell>
                                <TableCell style={{
                                    background: "linear-gradient(180deg, #1e3a8a 0%, #1e40af 100%)",
                                    color: 'white',
                                    fontWeight: '700',
                                    fontSize: '11.5px',
                                    textAlign: 'left',
                                    padding: '2px',
                                }}>IFSC Code</TableCell>
                                <TableCell style={{
                                    background: "linear-gradient(180deg, #1e3a8a 0%, #1e40af 100%)",
                                    color: 'white',
                                    fontWeight: '700',
                                    fontSize: '11.5px',
                                    textAlign: 'left',
                                    padding: '2px',
                                }}>Contact</TableCell>
                                <TableCell style={{
                                    background: "linear-gradient(180deg, #1e3a8a 0%, #1e40af 100%)",
                                    color: 'white',
                                    fontWeight: '700',
                                    fontSize: '11.5px',
                                    textAlign: 'center',
                                    padding: '2px',
                                }}>Actions</TableCell>
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={7} style={{ textAlign: 'center', padding: '40px' }}>
                                        <CircularProgress size={40} />
                                        <p style={{ marginTop: '16px', color: '#6b7280', fontSize: '14px' }}>Loading bank records...</p>
                                    </TableCell>
                                </TableRow>
                            ) : filteredData && filteredData.length > 0 ? (
                                filteredData.map((item, index) => (
                                    <TableRow key={item.Id} sx={{
                                        '&:hover': { backgroundColor: '#f0f9ff' },
                                        '&:nth-of-type(even)': { backgroundColor: '#fafafa' }
                                    }}>
                                        <TableCell sx={{ padding: '6px 4px', fontSize: '11.5px', textAlign: 'center' }}>
                                            {index + 1}
                                        </TableCell>
                                        <TableCell sx={{ padding: '6px 12px', fontSize: '11.5px' }}>
                                            {item.Name}
                                        </TableCell>
                                        <TableCell sx={{ padding: '6px 12px', fontSize: '11.5px' }}>
                                            {item.AccountNumber}
                                        </TableCell>
                                        <TableCell sx={{ padding: '6px 12px', fontSize: '11.5px' }}>
                                            {item.BranchName}
                                        </TableCell>
                                        <TableCell sx={{ padding: '6px 12px', fontSize: '11.5px' }}>
                                            {item.IFSCCode}
                                        </TableCell>
                                        <TableCell sx={{ padding: '6px 12px', fontSize: '11.5px' }}>
                                            {item.ContactNumber}
                                        </TableCell>
                                        <TableCell sx={{ padding: '6px 4px', textAlign: 'center' }}>
                                            <Tooltip title="Edit" arrow placement="top">
                                                <IconButton onClick={() => openDetailModal(item.Id)} sx={{ padding: '4px', marginRight: '4px' }}>
                                                    <EditIcon style={{ color: '#1e40af', fontSize: '16px' }} />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Delete" arrow placement="top">
                                                <IconButton onClick={() => openConfirmDialog(item.Id)} sx={{ padding: '4px' }}>
                                                    <DeleteIcon style={{ color: '#dc2626', fontSize: '16px' }} />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={7} style={{ textAlign: 'center', padding: '40px' }}>
                                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
                                            <span style={{ fontSize: '48px' }}>🏦</span>
                                            <p style={{ color: '#6b7280', fontSize: '14px', margin: 0 }}>No bank records found</p>
                                            <p style={{ color: '#9ca3af', fontSize: '12px', margin: 0 }}>Click "+ New Bank" to add your first bank</p>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

            {/* Delete Confirmation Dialog */}
            <Dialog open={confirmDialogOpen} onClose={closeConfirmDialog}>
                <DialogTitle>Delete Record?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete this bank record?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <button onClick={closeConfirmDialog} className="bg-red-600 text-white border border-red-600 rounded px-4 py-2 mr-2">
                        Cancel
                    </button>
                    <button onClick={handleDelete} className="bg-green-600 text-white border border-green-600 rounded px-4 py-2">
                        Confirm
                    </button>
                </DialogActions>
            </Dialog>

            {/* Edit Bank Modal */}
            {isDetailModalOpen && (
                <div className="fixed inset-0 flex items-center justify-center z-50 p-3" style={{ backgroundColor: 'rgba(0, 0, 0, 0.6)', backdropFilter: 'blur(4px)' }}>
                    <div className="bg-white w-full overflow-hidden" style={{
                        maxWidth: '900px',
                        maxHeight: '85vh',
                        borderRadius: '16px',
                        boxShadow: '0 20px 40px -12px rgba(0, 0, 0, 0.25)',
                    }}>
                        <div style={{
                            background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                            padding: '16px 24px',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            height: '55px',
                        }}>
                            <div style={{ height: '20px' }}>
                                <h6 style={{
                                    color: 'white',
                                    fontSize: '15px',
                                    fontWeight: '700',
                                    margin: 0,
                                    marginTop: '-10px'
                                }}>
                                    Edit Bank
                                </h6>
                                <p style={{
                                    color: 'rgba(255, 255, 255, 0.9)',
                                    fontSize: '13px',
                                    margin: '2px 0 0 0'
                                }}>
                                    Update Bank information
                                </p>
                            </div>
                            <button onClick={toggleModalGrp2} style={{
                                background: 'rgba(255, 255, 255, 0.2)',
                                border: 'none',
                                borderRadius: '50%',
                                width: '36px',
                                height: '36px',
                                cursor: 'pointer',
                                color: 'white',
                                fontSize: '22px',
                            }}>
                                ×
                            </button>
                        </div>

                        <div className="overflow-y-auto" style={{ maxHeight: 'calc(85vh - 150px)', padding: '16px 20px' }}>
                            {errorMessageEdit && (
                                <div style={{
                                    background: 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)',
                                    border: '1px solid #fca5a5',
                                    borderRadius: '12px',
                                    padding: '16px',
                                    marginBottom: '24px',
                                }}>
                                    <p style={{ color: '#991b1b', fontSize: '14px', margin: 0 }}>⚠️ {errorMessageEdit}</p>
                                </div>
                            )}

                            <form onSubmit={updateRecord}>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label style={{ display: 'block', color: '#374151', fontSize: '12px', fontWeight: '600', marginBottom: '6px' }}>
                                            Bank Name <span style={{ color: '#ef4444' }}>*</span>
                                        </label>
                                        <input
                                            type="text"
                                            name="Name"
                                            value={updatedData.Name || ''}
                                            onChange={handleUpdateInputChange}
                                            style={{
                                                width: '100%',
                                                padding: '7px 10px',
                                                border: '2px solid #e5e7eb',
                                                borderRadius: '8px',
                                                fontSize: '13px',
                                                height: '32px'
                                            }}
                                            placeholder="Enter bank name"
                                        />
                                    </div>

                                    <div>
                                        <label style={{ display: 'block', color: '#374151', fontSize: '12px', fontWeight: '600', marginBottom: '6px' }}>
                                            Account Number <span style={{ color: '#ef4444' }}>*</span>
                                        </label>
                                        <input
                                            type="text"
                                            name="AccountNumber"
                                            value={updatedData.AccountNumber || ''}
                                            onChange={handleUpdateInputChange}
                                            style={{
                                                width: '100%',
                                                padding: '7px 10px',
                                                border: '2px solid #e5e7eb',
                                                borderRadius: '8px',
                                                fontSize: '13px',
                                                height: '32px'
                                            }}
                                            placeholder="Enter account number"
                                        />
                                    </div>

                                    <div>
                                        <label style={{ display: 'block', color: '#374151', fontSize: '12px', fontWeight: '600', marginBottom: '6px' }}>
                                            Branch Name
                                        </label>
                                        <input
                                            type="text"
                                            name="BranchName"
                                            value={updatedData.BranchName || ''}
                                            onChange={handleUpdateInputChange}
                                            style={{
                                                width: '100%',
                                                padding: '7px 10px',
                                                border: '2px solid #e5e7eb',
                                                borderRadius: '8px',
                                                fontSize: '13px',
                                                height: '32px'
                                            }}
                                            placeholder="Enter branch name"
                                        />
                                    </div>

                                    <div>
                                        <label style={{ display: 'block', color: '#374151', fontSize: '12px', fontWeight: '600', marginBottom: '6px' }}>
                                            IFSC Code
                                        </label>
                                        <input
                                            type="text"
                                            name="IFSCCode"
                                            value={updatedData.IFSCCode || ''}
                                            onChange={handleUpdateInputChange}
                                            style={{
                                                width: '100%',
                                                padding: '7px 10px',
                                                border: '2px solid #e5e7eb',
                                                borderRadius: '8px',
                                                fontSize: '13px',
                                                height: '32px'
                                            }}
                                            placeholder="Enter IFSC code"
                                        />
                                    </div>

                                    <div>
                                        <label style={{ display: 'block', color: '#374151', fontSize: '12px', fontWeight: '600', marginBottom: '6px' }}>
                                            Address Line 1
                                        </label>
                                        <input
                                            type="text"
                                            name="AddressLine1"
                                            value={updatedData.AddressLine1 || ''}
                                            onChange={handleUpdateInputChange}
                                            style={{
                                                width: '100%',
                                                padding: '7px 10px',
                                                border: '2px solid #e5e7eb',
                                                borderRadius: '8px',
                                                fontSize: '13px',
                                                height: '32px'
                                            }}
                                            placeholder="Enter address line 1"
                                        />
                                    </div>

                                    <div>
                                        <label style={{ display: 'block', color: '#374151', fontSize: '12px', fontWeight: '600', marginBottom: '6px' }}>
                                            Address Line 2
                                        </label>
                                        <input
                                            type="text"
                                            name="AddressLine2"
                                            value={updatedData.AddressLine2 || ''}
                                            onChange={handleUpdateInputChange}
                                            style={{
                                                width: '100%',
                                                padding: '7px 10px',
                                                border: '2px solid #e5e7eb',
                                                borderRadius: '8px',
                                                fontSize: '13px',
                                                height: '32px'
                                            }}
                                            placeholder="Enter address line 2"
                                        />
                                    </div>

                                    <div>
                                        <label style={{ display: 'block', color: '#374151', fontSize: '12px', fontWeight: '600', marginBottom: '6px' }}>
                                            Contact Number
                                        </label>
                                        <input
                                            type="text"
                                            name="ContactNumber"
                                            value={updatedData.ContactNumber || ''}
                                            onChange={handleUpdateInputChange}
                                            style={{
                                                width: '100%',
                                                padding: '7px 10px',
                                                border: '2px solid #e5e7eb',
                                                borderRadius: '8px',
                                                fontSize: '13px',
                                                height: '32px'
                                            }}
                                            placeholder="Enter contact number"
                                        />
                                    </div>
                                </div>
                            </form>
                        </div>

                        <div style={{
                            background: 'linear-gradient(to right, #f9fafb, #f3f4f6)',
                            padding: '16px 24px',
                            display: 'flex',
                            justifyContent: 'flex-end',
                            gap: '12px',
                            borderTop: '1px solid #e5e7eb'
                        }}>
                            <button onClick={toggleModalGrp2} disabled={isUpdateLoading} style={{
                                padding: '8px 24px',
                                background: 'white',
                                border: '2px solid #e5e7eb',
                                borderRadius: '10px',
                                fontSize: '13px',
                                fontWeight: '600',
                                color: '#6b7280',
                                cursor: isUpdateLoading ? 'not-allowed' : 'pointer',
                            }}>
                                Cancel
                            </button>
                            <button onClick={updateRecord} disabled={isUpdateLoading} style={{
                                padding: '8px 24px',
                                background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                                border: 'none',
                                borderRadius: '10px',
                                fontSize: '13px',
                                fontWeight: '600',
                                color: 'white',
                                cursor: isUpdateLoading ? 'not-allowed' : 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                            }}>
                                {isUpdateLoading ? (
                                    <>
                                        <CircularProgress size={18} color="inherit" />
                                        <span>Updating...</span>
                                    </>
                                ) : (
                                    <>
                                        <span>✓</span>
                                        <span>Update Bank</span>
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

export default withSessionCheck(BankIndex);
